import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { DropdownMenusModule } from 'src/app/_metronic/partials';
import { FormImportNewComponent } from '../../import/components/form-import-new/form-import-new.component';
import {
  FormAddUserComponent,
  UserSaveAction,
  UserSaveFailedEvent,
} from '../../import/components/form-add-user/form-add-user.component';
import { UserManagementService } from 'src/app/services/api/user-management/user-management.service';
import {
  UserManagementDataSource,
  UserManagementDropdownOptions,
  UserManagementListResponse,
  UserManagementModel,
  UserManagementRow,
} from 'src/app/services/models/user-management/user-management.model';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { SharedModule } from 'src/shared/shared.module';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';

type UserManagementAction = 'download-template' | 'import' | 'create' | 'edit' | 'delete';

@Component({
  selector: 'app-users-management',
  imports: [
    SharedModule,
    TranslateModule,
    DropdownMenusModule,
    ReactiveFormsModule,
    FormImportNewComponent,
    FormAddUserComponent,
  ],
  templateUrl: './users-management.component.html',
  styleUrl: './users-management.component.scss',
})
export class UsersManagementComponent implements OnInit, OnDestroy {
  @ViewChild('rowIndexTemplate', { static: true })
  rowIndexTemplate!: TemplateRef<any>;
  @ViewChild('tableCellAction', { static: true })
  tableCellAction!: TemplateRef<any>;
  @ViewChild(FormImportNewComponent)
  importForm!: FormImportNewComponent;
  @ViewChild(FormAddUserComponent)
  userForm!: FormAddUserComponent;
  @ViewChild('deleteUserConfirmDialog')
  deleteUserConfirmDialog!: ConfirmDialogComponent;
  @ViewChild('deleteUserSuccessDialog')
  deleteUserSuccessDialog!: ConfirmDialogComponent;
  @ViewChild('deleteUserErrorDialog')
  deleteUserErrorDialog!: ConfirmDialogComponent;
  @ViewChild('createUserSuccessDialog')
  createUserSuccessDialog!: ConfirmDialogComponent;
  @ViewChild('createUserErrorDialog')
  createUserErrorDialog!: ConfirmDialogComponent;
  @ViewChild('editUserSuccessDialog')
  editUserSuccessDialog!: ConfirmDialogComponent;
  @ViewChild('editUserErrorDialog')
  editUserErrorDialog!: ConfirmDialogComponent;
  @ViewChild('importSuccessDialog')
  importSuccessDialog!: ConfirmDialogComponent;

  form!: FormGroup;
  columns: any[] = [];
  users = signal<UserManagementDataSource>(this.createUsersDataSource([]));
  dropdownOptions = signal<UserManagementDropdownOptions>({
    positions: [],
    departments: [],
    roles: [],
  });
  tableLoading = signal(false);
  lastAction = signal<UserManagementAction | null>(null);
  pageSizeOptions = [10, 20, 50];
  isDownloadingTemplate = false;
  isImporting = false;
  isDeleting = false;
  deleteUserErrorMessage = 'ไม่สามารถลบรายการได้ กรุณาลองใหม่อีกครั้ง';
  createUserErrorMessage = 'ไม่สามารถเพิ่มรายการได้ กรุณาลองใหม่อีกครั้ง';
  editUserErrorMessage = 'ไม่สามารถแก้ไขรายการได้ กรุณาลองใหม่อีกครั้ง';

  private fb = inject(FormBuilder);
  private userManagementService = inject(UserManagementService);
  private cdr = inject(ChangeDetectorRef);
  private currentPage = 1;
  private currentLimit = 10;
  private currentSortParams: DatatableSortParams = {
    sort: null,
    direction: null,
  };
  private deletingUserId?: number;
  private keywordSearchSubscription?: Subscription;
  private readonly sortFieldMap: DatatableSortFieldMap = {
    index: 'sequence',
    academicRank: 'academic_rank',
    fullName: 'full_name',
    email: 'email',
    position: 'position',
  };

  ngOnInit(): void {
    this.initForm();
    this.initColumns();
    this.listenKeywordSearch();
    this.loadDropdownOptions();
    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.keywordSearchSubscription?.unsubscribe();
  }

  onSearch(): void {
    this.loadUsers(1);
  }

  onFilterChange(): void {
    queueMicrotask(() => this.loadUsers(1));
  }

  onFilterSelectChange(controlName: 'position_id' | 'department_id' | 'role_id', selected: unknown): void {
    this.form.patchValue(
      { [controlName]: this.getSelectedOptionId(selected) },
      { emitEvent: false }
    );
    this.loadUsers(1);
  }

  onDownloadTemplate(): void {
    this.lastAction.set('download-template');

    if (this.isDownloadingTemplate) {
      return;
    }

    this.isDownloadingTemplate = true;
    this.userManagementService.exportUsers().subscribe({
      next: (blob) => this.downloadBlob(blob, `users_export_${this.getTodayDate()}.xlsx`),
      error: (err) => {
        console.error(err);
        this.isDownloadingTemplate = false;
      },
      complete: () => {
        this.isDownloadingTemplate = false;
      },
    });
  }

  onImport(): void {
    this.lastAction.set('import');
  }

  onImportFile(file: File): void {
    this.lastAction.set('import');

    if (this.isImporting) {
      return;
    }

    this.isImporting = true;
    this.userManagementService.importUsers(file).subscribe({
      next: () => {
        this.importForm.closeModal();
        this.importForm.clearFileSelection();
        this.loadUsers(1);
        this.importSuccessDialog.open();
      },
      error: (err) => {
        this.importForm.showImportError(err.error);
        this.isImporting = false;
      },
      complete: () => {
        this.isImporting = false;
      },
    });
  }

  onAddUser(): void {
    this.lastAction.set('create');
    this.userForm.openModal();
  }

  onEditUser(row: UserManagementRow): void {
    this.lastAction.set('edit');
    this.userForm.openModal(row.id);
  }

  onUserSaved(action: UserSaveAction): void {
    this.loadUsers(action === 'create' ? 1 : this.currentPage);

    if (action === 'create') {
      this.createUserSuccessDialog.open();
      return;
    }

    this.editUserSuccessDialog.open();
  }

  onUserSaveFailed({ action, message }: UserSaveFailedEvent): void {
    if (action === 'create') {
      this.openCreateUserErrorDialog(message);
      return;
    }

    this.openEditUserErrorDialog(message);
  }

  onDeleteUser(row: UserManagementRow): void {
    this.lastAction.set('delete');
    this.deletingUserId = row.id;
    this.deleteUserConfirmDialog.open();
  }

  onConfirmDeleteUser(): void {
    if (this.deletingUserId == null || this.isDeleting) {
      return;
    }

    this.isDeleting = true;
    this.userManagementService.deleteUser(this.deletingUserId).subscribe({
      next: () => {
        this.deleteUserConfirmDialog.close();
        this.deletingUserId = undefined;
        this.loadUsers();
        this.deleteUserSuccessDialog.open();
      },
      error: (err) => {
        console.error(err);
        this.deleteUserConfirmDialog.close();
        this.openDeleteUserErrorDialog(this.getDeleteUserErrorMessage(err));
        this.isDeleting = false;
      },
      complete: () => {
        this.isDeleting = false;
      },
    });
  }

  onCancelDeleteUser(): void {
    this.deletingUserId = undefined;
  }

  loadUsers(page = this.currentPage): void {
    this.tableLoading.set(true);
    this.currentPage = page;
    const { keywords, position_id, department_id, role_id } = this.form.getRawValue();

    this.userManagementService.getUsersList({
      keywords,
      position_id,
      department_id,
      role_id,
      page: this.currentPage,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort,
      direction: this.currentSortParams.direction,
    }).subscribe({
      next: (res) => {
        this.users.set(this.createUsersDataSource(res));
        this.tableLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.users.set(this.createUsersDataSource([]));
        this.tableLoading.set(false);
      },
    });
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.users().total_page || page === this.currentPage) {
      return;
    }

    this.loadUsers(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadUsers(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadUsers(1);
  }

  private initForm(): void {
    this.form = this.fb.group({
      keywords: [''],
      position_id: [null],
      department_id: [null],
      role_id: [null],
    });
  }

  private listenKeywordSearch(): void {
    this.keywordSearchSubscription = this.form.get('keywords')?.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(() => this.loadUsers(1));
  }

  private getSelectedOptionId(selected: unknown): number | string | null {
    if (selected == null || selected === '') {
      return null;
    }

    if (typeof selected === 'number' || typeof selected === 'string') {
      return selected;
    }

    if (typeof selected === 'object') {
      const option = selected as { id?: unknown; value?: unknown };
      const id = option.id ?? option.value;

      if (typeof id === 'number' || typeof id === 'string') {
        return id;
      }
    }

    return null;
  }

  private initColumns(): void {
    this.columns = [
      {
        prop: 'index',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.rowIndexTemplate,
        width: 64,
        minWidth: 64,
        maxWidth: 64,
      },
      {
        prop: 'academicRank',
        name: 'ตำแหน่งทางวิชาการ',
        sortable: true,
        width: 170,
        minWidth: 170,
      },
      {
        prop: 'fullName',
        name: 'ชื่อ - นามสกุล',
        sortable: true,
        width: 180,
        minWidth: 180,
      },
      {
        prop: 'email',
        name: 'อีเมล',
        sortable: true,
        width: 220,
        minWidth: 220,
        cellClass: 'users-management-email-cell',
      },
      {
        prop: 'position',
        name: 'ตำแหน่ง',
        sortable: true,
        width: 180,
        minWidth: 180,
      },
      {
        prop: 'department',
        name: 'สาขาวิชา',
        sortable: false,
        width: 170,
        minWidth: 170,
      },
      {
        prop: 'roles',
        name: 'บทบาทการใช้งาน',
        sortable: false,
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.tableCellAction,
        width: 104,
        minWidth: 104,
        maxWidth: 104,
        cellClass: 'action-cell',
        headerClass: 'action-header',
      },
    ];
  }

  private loadDropdownOptions(): void {
    this.userManagementService.getDropdownOptions().subscribe({
      next: (options) => this.dropdownOptions.set(options),
      error: (err) => console.error(err),
    });
  }

  private createUsersDataSource(
    response: UserManagementListResponse | UserManagementModel[]
  ): UserManagementDataSource {
    const items = Array.isArray(response) ? response : response.data;
    const page = Array.isArray(response) ? 1 : response.page;
    const limit = Array.isArray(response) ? this.currentLimit : response.limit;
    const total = Array.isArray(response) ? items.length : response.total;
    const totalPage = Array.isArray(response) ? Math.max(1, Math.ceil(total / limit)) : response.total_pages;
    const data = items.map((item) => ({
      id: item.id,
      sequence: item.sequence,
      academicRank: item.academic_rank ?? '-',
      fullName: item.full_name,
      email: item.email,
      position: item.position ?? '-',
      department: item.department.name ,
      roles: item.roles.map((role) => role.label).join(', ') || '-',
    }));

    return {
      data,
      limit,
      offset: (page - 1) * limit,
      page,
      total,
      total_page: totalPage,
    };
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();

    window.URL.revokeObjectURL(url);
  }

  private getTodayDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  private openDeleteUserErrorDialog(message: string): void {
    this.deleteUserErrorMessage = message;
    this.cdr.detectChanges();
    this.deleteUserErrorDialog.open();
  }

  private openCreateUserErrorDialog(message: string): void {
    this.createUserErrorMessage = message;
    this.cdr.detectChanges();
    this.createUserErrorDialog.open();
  }

  private openEditUserErrorDialog(message: string): void {
    this.editUserErrorMessage = message;
    this.cdr.detectChanges();
    this.editUserErrorDialog.open();
  }

  private getDeleteUserErrorMessage(error: unknown): string {
    const fallbackMessage = 'ไม่สามารถลบรายการได้ กรุณาลองใหม่อีกครั้ง';
    const responseError = (error as { error?: unknown } | null | undefined)?.error;
    const parsedResponseError = this.parseErrorResponse(responseError);

    if (parsedResponseError != null && typeof parsedResponseError === 'object') {
      const errorRecord = parsedResponseError as { errors?: unknown; error?: unknown; message?: unknown };

      if (Array.isArray(errorRecord.errors) && errorRecord.errors.length > 0) {
        const errors = errorRecord.errors
          .filter((item): item is string => typeof item === 'string' && item.trim() !== '')
          .map((item) => item.trim());

        if (errors.length > 0) {
          return errors.join('\n');
        }
      }

      const errorText = this.getErrorText(errorRecord.error);
      const messageText = this.getErrorText(errorRecord.message);
      const message = errorText !== '' ? errorText : messageText;

      return message !== '' ? message : fallbackMessage;
    }

    const responseErrorText = this.getErrorText(parsedResponseError);
    const messageText = this.getErrorText((error as { message?: unknown } | null | undefined)?.message);
    const message = responseErrorText !== '' ? responseErrorText : messageText;

    return message !== '' ? message : fallbackMessage;
  }

  private parseErrorResponse(value: unknown): unknown {
    if (typeof value !== 'string') {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  private getErrorText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }
}
